/**
 * Impersonation context — shared sessionStorage-backed token + helpers used by:
 *   • axios interceptor (route chef-side API calls through the impersonation JWT)
 *   • ImpersonationBanner (visible read-only indicator + Exit button)
 *   • HQC77 ticket drawer (start the session)
 */
const STORAGE_KEY = "pp_impersonation";

export const ImpersonationStore = {
  read() {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (data.expires_at && data.expires_at * 1000 < Date.now()) {
        window.sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return data;
    } catch {
      return null;
    }
  },
  write(data) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      window.dispatchEvent(new CustomEvent("pp-impersonation-change"));
    } catch {
      /* ignore quota */
    }
  },
  clear() {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
      window.dispatchEvent(new CustomEvent("pp-impersonation-change"));
    } catch {
      /* ignore */
    }
  },
};

export const isAdminApiPath = (url) => {
  if (!url) return false;
  return url.includes("/api/admin/") || url.includes("/api/webhook/");
};

/**
 * Axios request interceptor — when an impersonation token is active, route
 * chef-side calls through it instead of the chef's normal Supabase JWT.
 * Admin endpoints (/api/admin/*) continue using the staff JWT.
 */
export const installImpersonationInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use((config) => {
    const imp = ImpersonationStore.read();
    if (!imp || !imp.access_token) return config;
    if (isAdminApiPath(config.url)) return config;  // keep admin JWT for admin paths
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${imp.access_token}`;
    return config;
  });
};
